/**
 * Utility functions for downloading and sharing conversations
 */
import { CtyShowToast, checkType } from '../../index';

export interface ConversationMessage {
  message: string;
  role?: string;
  isUser?: boolean;
  isAdvisor?: boolean;
  timestamp?: string;
}

/**
 * Generates a text file from conversation messages
 */
export function generateConversationTextFile(
  messages: ConversationMessage[],
  advisorName: string,
  timestamp: string
): string {
  const header = `Conversation with ${advisorName} at ${timestamp}\n\n`;
  
  const conversationText = messages
    .map((msg) => {
      if (msg.role === 'user' || msg.isUser) {
        return `User: ${msg.message?.trim() ?? ''}`;
      } else if (msg.role === 'bot' || msg.isAdvisor) {
        return `${advisorName}: ${msg.message?.trim() ?? ''}`;
      }
      return '';
    })
    .filter(Boolean)
    .join('\n\n');
  
  return `${header}${conversationText}`;
}

/**
 * Downloads a text file
 */
export function downloadTextFile(content: string, filename: string): void {
  try {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to download text file:', error);
    CtyShowToast(checkType.error, 'Failed to download text file');
  }
}

/**
 * Downloads a file from a presigned URL
 * Uses the same mechanism as Knowledge Sources download
 */
export async function downloadFileFromUrl(url: string, filename: string): Promise<void> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.statusText}`);
    }
    
    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Failed to download file:', error);
    CtyShowToast(checkType.error, 'Failed to download file');
    throw error;
  }
}

/**
 * Sends email with attachment using Web Share API (when available) or mailto fallback
 * Web Share API allows direct file attachment in supported browsers
 */
export async function sendEmailWithAttachment(
  subject: string,
  body: string,
  file: File
): Promise<boolean> {
  // Check if Web Share API is available and supports files
  if (navigator.share && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({
        title: subject,
        text: body,
        files: [file]
      });
      return true;
    } catch (error: any) {
      // User cancelled or error occurred
      if (error.name !== 'AbortError') {
        console.error('Web Share API failed:', error);
      }
      // Fall through to mailto fallback
    }
  }

  // Fallback to mailto (doesn't support attachments, but opens email client)
  // For desktop Outlook, user will need to manually attach the downloaded file
  const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  globalThis.location.href = mailtoLink;
  
  // Download the file so user can attach it manually in desktop email clients
  const url = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  
  return false;
}

/**
 * Creates a File object from text content
 */
export function createTextFile(content: string, filename: string): File {
  const blob = new Blob([content], { type: 'text/plain' });
  return new File([blob], filename, { type: 'text/plain' });
}

/**
 * Creates a File object from a blob URL (for PDFs)
 */
export async function createFileFromUrl(url: string, filename: string, mimeType: string = 'application/pdf'): Promise<File> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${response.statusText}`);
  }
  const blob = await response.blob();
  return new File([blob], filename, { type: mimeType });
}
